import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import applyCaseMiddleware from 'axios-case-converter'

import { useToken } from '@/modules/auth'

import useCaseConverter from '../hooks/useCaseConverter'
import { HttpInstance, InstanceParams } from '../models/httpInstance.model'
import IInstanceService from './instance.interface'

export default class InstanceService implements IInstanceService {
  /**
   * createInstace method
   * Wrap instace creator with
   * - case middleware
   * - Authorization token interceptor
   * - get/post/put/delete methods
   *
   * All params are optional
   * - baseURL: url base for ehr system or non baseUrl to pass url in request
   * - timeout: timeout of request
   * - withToken: default true. If it's true, will add request interceptor in instance with Autorization token
   * - applyCaseMiddlewareOnRequest: default is true. Disable middleware case for request only (ex. for external api)
   *
   * @param InstanceParams
   * @returns instance
   */
  createInstance({
    baseURL,
    timeout,
    withToken = true,
    applyCaseMiddlewareOnRequest = true
  }: InstanceParams): HttpInstance {
    const axiosInstance = this.createInstanceWithMiddleware(
      baseURL,
      timeout,
      applyCaseMiddlewareOnRequest
    )
    if (withToken) {
      axiosInstance.interceptors.request.use(
        async request => {
          const { getToken } = useToken()
          const token = await getToken()
          request.headers.Authorization = `Bearer ${token}`
          return request
        },
        (error: Error) => {
          return Promise.reject(error)
        }
      )
    }

    return {
      get: async (url: string, options?: AxiosRequestConfig) => {
        try {
          const response = await axiosInstance.get(url, options)
          return {
            hasError: false,
            data: response?.data
          }
        } catch (error) {
          return {
            hasError: true,
            data: null,
            error
          }
        }
      },
      put: async (url: string, data: object, options?: AxiosRequestConfig) => {
        try {
          const response = await axiosInstance.put(url, data, options)
          return {
            hasError: false,
            data: response?.data
          }
        } catch (error) {
          return {
            hasError: true,
            data: null,
            error
          }
        }
      },
      post: async (
        url: string,
        data?: object,
        options?: AxiosRequestConfig
      ) => {
        try {
          const response = await axiosInstance.post(url, data, options)
          return {
            hasError: false,
            data: response?.data
          }
        } catch (error) {
          return {
            hasError: true,
            data: null,
            error
          }
        }
      },
      delete: async (
        url: string,
        data?: object,
        options?: AxiosRequestConfig
      ) => {
        try {
          const response = await axiosInstance.delete(url, {
            ...options,
            data: data
          })
          return {
            hasError: false,
            data: response?.data
          }
        } catch (error) {
          return {
            hasError: true,
            data: null,
            error
          }
        }
      }
    }
  }

  /**
   * apply moddleware case according parameters and return instance
   * @param baseURL: default url prefix, if have one
   * @param timeout: timeout of request
   * @param applyCaseMiddlewareOnRequest: define if apply case middleware in request. For external endpoints
   * the middleware case is removed
   * @returns axios instance
   */
  private createInstanceWithMiddleware(
    baseURL?: string,
    timeout?: number,
    applyCaseMiddlewareOnRequest = true
  ): AxiosInstance {
    const createAxios = () =>
      axios.create({
        baseURL,
        timeout,
        headers: {
          'Content-Type': 'application/json'
        }
      })

    let axiosInstance: AxiosInstance

    const middlewareOptions = useCaseConverter()

    if (applyCaseMiddlewareOnRequest) {
      axiosInstance = applyCaseMiddleware(createAxios(), middlewareOptions)
    } else {
      axiosInstance = applyCaseMiddleware(createAxios(), {
        caseMiddleware: {
          requestTransformer: config => {
            // Disable case transformation for request
            return config
          }
        },
        ...middlewareOptions
      })
    }
    return axiosInstance
  }
}
